/**
 * Lab Mode API Hooks
 * 
 * Custom hooks for interacting with Lab Mode endpoints
 */

import { useState, useEffect } from 'react';
import api from '../services/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Get token from storage (same as other services)
const getToken = () => localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');

/**
 * Hook to fetch Lab Mode status (flags + consent)
 */
export const useLabStatus = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchStatus = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = getToken();
      const response = await fetch(`${BACKEND_URL}/api/lab/status`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.status === 403) {
        throw new Error('LAB_DISABLED');
      }

      if (!response.ok) {
        throw new Error('Failed to fetch lab status');
      }

      const data = await response.json();
      setStatus(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  return { status, loading, error, refetch: fetchStatus };
};

/**
 * Hook to fetch Lab Mode programs configuration
 */
export const useLabPrograms = () => {
  const [programs, setPrograms] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPrograms = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = getToken();
      const response = await fetch(`${BACKEND_URL}/api/lab/programs`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('FETCH_PROGRAMS_FAILED');
      }

      const data = await response.json();
      setPrograms(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPrograms();
  }, []);

  return { programs, loading, error, refetch: fetchPrograms };
};

/**
 * Hook to update Lab Mode configuration
 */
export const useUpdateLab = () => {
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState(null);

  const updateLab = async (payload) => {
    try {
      setUpdating(true);
      setError(null);
      const token = getToken();

      const response = await fetch(`${BACKEND_URL}/api/lab/config`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to update lab configuration');
      }

      const data = await response.json();
      return data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setUpdating(false);
    }
  };

  return { updateLab, updating, error };
};

/**
 * Hook to record pitch-match measurement
 */
export const usePitchMatch = () => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const savePitchMatch = async (payload) => {
    try {
      setSaving(true);
      setError(null);
      const token = getToken();

      const response = await fetch(`${BACKEND_URL}/api/lab/pitch-match`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to save pitch match');
      }

      const data = await response.json();
      return data; // { ok: true, last: { f_t, ... }, avg_f_t: number }
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setSaving(false);
    }
  };

  return { savePitchMatch, saving, error };
};
